// AssetRow — one capture-item row on the RoomCaptureScreen (buildspec
// 20-ui §5.5, PRD §7.3.1).
//
// Composition rather than inheritance: the row picks `<AssetCounter>`
// or `<MeasurementInput>` from the item's `unitOfMeasure` and layers
// the `<ZeroConfirmToggle>` on top when the recorded quantity is zero.
// The capture-client dispatch is NOT wired here — the row emits a
// patch via `onUpdate`, and the containing room screen (C3.x) calls
// the client adapter (C2.7 `createCaptureClient`). Keeping dispatch
// out of this component means the row stays a pure presentational
// surface that can be unit-tested without MSW or transport seams, and
// that `updateItem` payload assembly (itemId, syncVersion, notes) lives
// with the session store which already owns that state.
//
// Patch shape matches the subset of `CaptureMutation.updateItem` the
// row can produce: `quantity` as the canonical decimal string (string
// keeps numeric(10,2) precision parity with the server — see
// `@citycare/shared/capture/dtos.ts`), and `isZeroConfirmed`. Either or
// both may be present.
//
// "Zero" detection is UoM-aware (20-ui §5.3 + §5.5):
//   - Count: integer-parsed quantity === 0 (non-numeric → treated as 0).
//   - Measurement: empty string OR parsed float === 0.
// This mirrors the PRD §7.4.1 distinction between tally and metric
// capture.
//
// Natural-revert UX: tapping `+` on a confirmed row increments the
// counter AND clears `isZeroConfirmed` in the same patch so the
// confirmed styling vanishes at the same moment the count lifts off 0
// (20-ui §5.3). No extra tap required from the user.

import { useState, type ReactElement } from 'react'

import { CheckCircle2 } from 'lucide-react'

import type { CaptureItemRow, UnitOfMeasure } from '@citycare/shared'

import { NumericKeypadSheet } from '../../ui/numeric-keypad-sheet'

import { AssetCounter } from './asset-counter'
import { MeasurementInput } from './measurement-input'
// ZeroConfirmToggle is no longer rendered here per product
// direction: a row left at zero is treated as captured-as-zero
// implicitly. The component itself remains in the codebase for any
// future surface that wants to expose explicit zero confirmation.

export interface AssetRowPatch {
  quantity?: string
  isZeroConfirmed?: boolean
}

export interface AssetRowProps {
  item: CaptureItemRow
  onUpdate: (patch: AssetRowPatch) => void
  // Fired when the surveyor taps `−` while the count is already at 0
  // — the room-capture flow uses this to remove the asset from the
  // visible list for this room instance. Only meaningful for Count
  // UoM rows; ignored for measurements (no `−` button to overshoot).
  onRemove?: () => void
  // 'compact' tightens padding + drops the inline "system › sub-system"
  // meta line and shrinks the counter buttons to the 48 px size.
  // RoomCaptureScreen passes 'compact' because the parent SystemGroup
  // already displays the system + sub-system context above each row.
  // SiteCaptureScreen renders rows in a flat list with no grouping
  // header so it leaves the variant at 'default' to keep the meta line.
  variant?: 'default' | 'compact'
}

const UOM_DISPLAY: Record<UnitOfMeasure, string> = {
  Count: 'Count',
  m: 'm',
  m2: 'm²',
  m3: 'm³',
}

function isCount(uom: UnitOfMeasure): uom is 'Count' {
  return uom === 'Count'
}

function parseCountQuantity(value: string): number {
  const n = Number.parseInt(value, 10)
  if (!Number.isFinite(n) || n < 0) return 0
  return n
}

function parseMeasurementQuantity(value: string): number {
  if (value === '') return 0
  const n = Number.parseFloat(value)
  return Number.isFinite(n) ? n : 0
}

export function AssetRow({
  item,
  onUpdate,
  onRemove,
  variant = 'default',
}: AssetRowProps): ReactElement {
  const uom = item.unitOfMeasure
  const quantityIsZero = isCount(uom)
    ? parseCountQuantity(item.quantity) === 0
    : parseMeasurementQuantity(item.quantity) === 0
  const showPending = item.isFieldAdded && item.requiresReview

  const [countKeypadOpen, setCountKeypadOpen] = useState(false)

  const handleCountChange = (next: number): void => {
    const patch: AssetRowPatch = { quantity: String(next) }
    // Natural revert: bumping above zero always clears the zero-confirm
    // flag in the same patch so the row un-greys at the same tap that
    // lifted the count off 0 (20-ui §5.3).
    if (item.isZeroConfirmed && next > 0) patch.isZeroConfirmed = false
    onUpdate(patch)
  }

  // Manual numeric entry for Count rows. The keypad value is a string;
  // we route through the existing handleCountChange so zero-confirm
  // reversion stays in lockstep with the +/- and hold paths.
  const handleCountManualEntry = (next: string): void => {
    handleCountChange(parseCountQuantity(next))
  }

  const handleMeasurementChange = (next: string): void => {
    const patch: AssetRowPatch = { quantity: next }
    if (item.isZeroConfirmed && parseMeasurementQuantity(next) > 0) {
      patch.isZeroConfirmed = false
    }
    onUpdate(patch)
  }

  // The explicit zero-confirm toggle has been retired from this row;
  // the underlying `isZeroConfirmed` field is still tracked so any
  // historical confirmed-zero state continues to render (line-through
  // name + check icon). New rows simply rely on quantity values.
  const isConfirmedZero = item.isZeroConfirmed && quantityIsZero
  const rowAriaLabel = isConfirmedZero
    ? `${item.assetTypeName}, zero confirmed`
    : undefined
  const isCompact = variant === 'compact'

  return (
    <article
      data-testid="asset-row"
      data-uom={uom}
      data-zero-confirmed={item.isZeroConfirmed ? 'true' : 'false'}
      data-field-added={item.isFieldAdded ? 'true' : 'false'}
      data-pending-review={showPending ? 'true' : 'false'}
      aria-label={rowAriaLabel}
      className={[
        // Single-row inline layout: asset name (and optional meta /
        // pending pill) on the left, counter / measurement on the
        // right. The zero-confirm toggle drops to a second right-
        // aligned row only when the row is visibly at zero. This
        // keeps the row half the height of the previous stacked
        // layout without sacrificing the 44 px tap targets on the
        // counter buttons.
        'rounded-xl border border-[color:var(--color-cc-forest)]/10',
        isCompact ? 'p-2.5' : 'p-4',
        // Pending-review (W3.2) and zero-confirmed (W3.1) visual
        // treatments compose: pending-review's brand border + Spring
        // Wood tint takes precedence on the background; the line-
        // through + CheckCircle2 on the asset-name h3 keeps the
        // confirmed-zero signal intact. `bg-gray-50` is retained on
        // confirmed-zero rows for the existing behavioural contract
        // (a soft tint very close to Citycare Cream).
        showPending
          ? 'border-l-4 border-l-[color:var(--color-brand)] bg-[color:var(--color-surface)]'
          : isConfirmedZero
            ? 'bg-gray-50'
            : 'bg-white',
        item.isZeroConfirmed ? 'opacity-60' : '',
      ]
        .filter((c) => c !== '')
        .join(' ')}
    >
      <div
        className={[
          'flex items-center',
          isCompact ? 'gap-2' : 'gap-3',
        ].join(' ')}
      >
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <h3
            className={[
              'flex items-center gap-1.5 font-display font-semibold',
              isCompact ? 'text-sm' : 'text-base',
              isConfirmedZero
                ? 'text-gray-500 line-through'
                : 'text-gray-900',
            ].join(' ')}
          >
            {isConfirmedZero ? (
              <CheckCircle2
                aria-hidden="true"
                size={isCompact ? 14 : 16}
                className="shrink-0 text-[color:var(--color-cc-forest)]"
              />
            ) : null}
            <span className="break-words">{item.assetTypeName}</span>
          </h3>
          {showPending ? (
            <span
              data-testid="asset-row-pending-review"
              className="inline-flex w-fit items-center gap-1.5 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800"
            >
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-[color:var(--color-cc-orange)]" />
              Pending review
            </span>
          ) : null}
          {/* The system › sub-system meta line is duplicate noise
               inside SystemGroup, which already shows that lineage
               in its own headers. Site-level capture (no grouping)
               keeps it. */}
          {!isCompact ? (
            <p className="text-[11px] uppercase tracking-wider text-[color:var(--color-cc-charcoal)]/60">
              {item.systemName} &rsaquo; {item.subSystemName} ·{' '}
              {UOM_DISPLAY[uom]}
            </p>
          ) : null}
        </div>
        <div className="shrink-0">
          {isCount(uom) ? (
            <AssetCounter
              value={parseCountQuantity(item.quantity)}
              onChange={handleCountChange}
              onRemoveAtZero={onRemove}
              onRequestManualEntry={() => setCountKeypadOpen(true)}
              assetName={item.assetTypeName}
              ariaLabel={`${item.assetTypeName} quantity`}
              size={variant === 'compact' ? 'compact' : 'default'}
            />
          ) : (
            <MeasurementInput
              value={item.quantity}
              onChange={handleMeasurementChange}
              unit={uom}
              ariaLabel={`${item.assetTypeName} measurement`}
            />
          )}
        </div>
      </div>
      {isCount(uom) ? (
        <NumericKeypadSheet
          open={countKeypadOpen}
          onClose={() => setCountKeypadOpen(false)}
          value={String(parseCountQuantity(item.quantity))}
          onChange={handleCountManualEntry}
          decimal={false}
          ariaLabel={`${item.assetTypeName} quantity`}
          title={item.assetTypeName}
        />
      ) : null}
    </article>
  )
}
